/* shadcn/ui — vendored MIT source. Upstream: ui.shadcn.com (components/ui/chart).
   The Recharts wrapper: ChartContainer, ChartTooltip(+Content) and
   ChartLegend(+Content), plus the <style> block that turns a ChartConfig
   into per-series CSS variables.

   TWO DELIBERATE DIVERGENCES FROM UPSTREAM, both forced by where this runs:

   1. THE STYLE BLOCK IS SCOPED TO THE CONTAINER, NOT TO document.  Upstream
      emits `[data-chart=id] { --color-x: ... }` into a <style> that relies on
      an id selector resolving document-wide.  This island mounts inside a
      SHADOW ROOT, so the same <style> lands inside that root — which is what
      we want (it cannot leak) and works unchanged, because the container
      carrying data-chart is inside the same root.

   2. NO THEME MAP.  Upstream's ChartConfig supports {theme: {light, dark}}
      per series so a chart can carry its own light/dark colours.  This
      island must never do that: colour comes from --chart-1..5, which the
      brand compiler already resolves per theme.  A per-series theme map here
      would be a second source of truth for the same fact.  Only `color` is
      supported, and every call site passes a var(--chart-N) reference. */
import * as React from 'react';
/* NAMED imports, where upstream shadcn uses a namespace import. A namespace
   import pulls Recharts' whole entry graph in because nothing can be shaken
   out of it; naming the three pieces this island actually uses lets Rollup
   drop every chart type it does not. The measured saving on this bundle is
   recorded in the dispatch. */
import { ResponsiveContainer, Tooltip, Legend, type LegendProps } from 'recharts';
import { cn } from '../lib/utils';

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
    color?: string;
  };
};

type ChartContextProps = { config: ChartConfig };

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);
  if (!context) throw new Error('useChart must be used within a <ChartContainer />');
  return context;
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    config: ChartConfig;
    children: React.ComponentProps<typeof ResponsiveContainer>['children'];
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = React.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={cn(
          "flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector]:outline-none [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-none",
          className
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = 'Chart';

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(([, c]) => c.color);
  if (!colorConfig.length) return null;
  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `[data-chart=${id}] {\n${colorConfig
          .map(([key, itemConfig]) => `  --color-${key}: ${itemConfig.color};`)
          .join('\n')}\n}`
      }}
    />
  );
};

const ChartTooltip = Tooltip;

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof Tooltip> &
    React.ComponentProps<'div'> & {
      hideLabel?: boolean;
      hideIndicator?: boolean;
      nameKey?: string;
      formatter?: (value: any, name: any, item: any, index: number, payload: any) => React.ReactNode;
    }
>(
  (
    { active, payload, className, hideLabel = false, hideIndicator = false, label, formatter, nameKey },
    ref
  ) => {
    const { config } = useChart();
    if (!active || !payload?.length) return null;

    return (
      <div
        ref={ref}
        className={cn(
          'grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-popover px-2.5 py-1.5 text-xs shadow-xl text-popover-foreground',
          className
        )}
      >
        {!hideLabel ? <div className="font-medium">{label}</div> : null}
        <div className="grid gap-1.5">
          {payload.map((item: any, index: number) => {
            const key = `${nameKey || item.name || item.dataKey || 'value'}`;
            const itemConfig = config[key];
            const indicatorColor = item.payload?.fill || item.color;
            return (
              <div
                key={key}
                className="flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground"
              >
                {formatter && item?.value !== undefined && item.name ? (
                  formatter(item.value, item.name, item, index, item.payload)
                ) : (
                  <>
                    {!hideIndicator ? (
                      <div
                        className="shrink-0 rounded-[2px] h-2.5 w-2.5"
                        style={{ backgroundColor: indicatorColor }}
                      />
                    ) : null}
                    <div className="flex flex-1 justify-between leading-none items-center">
                      <span className="text-muted-foreground">{itemConfig?.label || item.name}</span>
                      {item.value !== undefined && (
                        <span className="font-mono font-medium tabular-nums text-foreground">
                          {item.value.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);
ChartTooltipContent.displayName = 'ChartTooltipContent';

const ChartLegend = Legend;

const ChartLegendContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> &
    Pick<LegendProps, 'payload' | 'verticalAlign'> & {
      hideIcon?: boolean;
      nameKey?: string;
    }
>(({ className, hideIcon = false, payload, verticalAlign = 'bottom', nameKey }, ref) => {
  const { config } = useChart();
  if (!payload?.length) return null;

  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-center gap-4 flex-wrap',
        verticalAlign === 'top' ? 'pb-3' : 'pt-3',
        className
      )}
    >
      {payload.map((item: any) => {
        /* For a Pie, the legend item's own `value` IS the nameKey value
           ('active'), while `dataKey` is the numeric key ('value'). Looking
           the config up under dataKey therefore misses every time and the
           legend falls through to the raw series key — which is what it did
           until this line was fixed. Read the nameKey off the datum first,
           exactly as upstream's getPayloadConfigFromPayload does. */
        const nk = `${nameKey || item.dataKey || 'value'}`;
        const key = `${item.payload?.[nk] ?? item.value ?? nk}`;
        const itemConfig = config[key];
        return (
          <div key={item.value} className="flex items-center gap-1.5">
            {itemConfig?.icon && !hideIcon ? (
              <itemConfig.icon />
            ) : (
              <div className="h-2 w-2 shrink-0 rounded-[2px]" style={{ backgroundColor: item.color }} />
            )}
            {itemConfig?.label ?? key}
          </div>
        );
      })}
    </div>
  );
});
ChartLegendContent.displayName = 'ChartLegendContent';

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartStyle
};
